import React, { useEffect, useState } from 'react';
import { useAppUI } from 'src/context/AppUIProvider/useAppUI';
import useAuthContext from 'src/context/AuthProvider/useAuthContext';

import { NotificationStatus } from 'src/context/AppLaunchNotificationsProvider/AppLaunchNotificationsProvider.types';
import {
  AppLaunchNotificationsContext,
  initialLaunchNotificationsStatus,
} from 'src/context/AppLaunchNotificationsProvider/useAppLaunchNotifications';
import useStoreSlug from 'src/common/hooks/useStoreSlug';
import { ISOStringToTime } from 'src/utils/time/ISOStringToTime';
import { useStaticFlags } from 'src/common/hooks/useStaticFlags/useStaticFlags';

export const AppLaunchNotificationProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { headerAlert } = useAppUI();
  const { user, isB2b } = useAuthContext();

  const { LAUNCH_NOTIFICATIONS } = useStaticFlags();

  const {
    isAvailable: isStoreAvailable,
    userStoreSlug,
    nextOpen,
  } = useStoreSlug();
  const [notificationStatus, setNotificationStatus] =
    useState<NotificationStatus>(initialLaunchNotificationsStatus);

  const formattedNextOpen = ISOStringToTime(nextOpen);

  const showStoreClosedNotification = () => {
    if (!LAUNCH_NOTIFICATIONS) return;

    const description =
      formattedNextOpen && LAUNCH_NOTIFICATIONS.storeClosed.nextOpenDescription
        ? LAUNCH_NOTIFICATIONS.storeClosed.nextOpenDescription.replace(
            '{time}',
            formattedNextOpen
          )
        : LAUNCH_NOTIFICATIONS.storeClosed.description;

    headerAlert.show({
      title: LAUNCH_NOTIFICATIONS.storeClosed.title,
      description,
    });
    setNotificationStatus((prev) => ({
      ...prev,
      storeClosedOpen: true,
    }));
  };

  const showOutOfCoverageNotification = () => {
    if (!LAUNCH_NOTIFICATIONS) return;

    headerAlert.show({
      title: LAUNCH_NOTIFICATIONS.outOfCoverage.title,
      description: LAUNCH_NOTIFICATIONS.outOfCoverage.description,
    });
    setNotificationStatus((prev) => ({
      ...prev,
      outOfCoverageOpen: true,
    }));
  };

  const resetNotificationStatus = () => {
    headerAlert.hide();
    setNotificationStatus((prev) => ({
      ...prev,
      storeClosedOpen: false,
      outOfCoverageOpen: false,
    }));
  };

  useEffect(() => {
    if (!user?.main_address || !LAUNCH_NOTIFICATIONS) {
      return;
    }

    const isDeliverySchedulingAvailable = isB2b;
    const shouldShowStoreClosed =
      userStoreSlug && !isStoreAvailable && !isDeliverySchedulingAvailable;
    const shouldShowOutOfCoverage = user.main_address && !userStoreSlug;

    if (shouldShowStoreClosed) {
      showStoreClosedNotification();
    } else if (shouldShowOutOfCoverage) {
      showOutOfCoverageNotification();
    } else {
      resetNotificationStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    user?.main_address,
    isStoreAvailable,
    isB2b,
    formattedNextOpen,
    userStoreSlug,
  ]);

  return (
    <AppLaunchNotificationsContext.Provider value={notificationStatus}>
      {children}
    </AppLaunchNotificationsContext.Provider>
  );
};
